import { Chat } from '@ai-sdk/react';
import {
  ChatOnDataCallback,
  ChatOnErrorCallback,
  ChatOnFinishCallback,
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithApprovalResponses,
  lastAssistantMessageIsCompleteWithToolCalls,
} from 'ai';
import config from '../../../config';
import { getRachelRequestHeaders } from '../hooks/useRachelChat.utils';
import { RachelUIMessage } from '../rachel.types';
import { findAnsweredAskUserPart } from '../rachelAskUser.utils';

export type RachelChatOnFinishCallback = (
  event: Parameters<ChatOnFinishCallback<RachelUIMessage>>[0],
  chatId: string | null,
) => void;

export interface RachelChatCallbacks {
  onData?: ChatOnDataCallback<RachelUIMessage>;
  onError?: ChatOnErrorCallback;
  onFinish?: RachelChatOnFinishCallback;
}

const DRAFT_CHAT_ID = 'rachel-draft';
const API = `${config.baseURL}/rachel/chat`;

/**
 * Uma instância `Chat` por chatId, fora do React: trocar de chat na tela troca a
 * instância exibida sem parar o stream das outras, e o provider pode desmontar
 * (resize, navegação) sem matar turnos. Os callbacks vivem num registro único
 * porque dependem de hooks e a instância pode nascer antes do hook renderizar.
 */
const instances = new Map<string, Chat<RachelUIMessage>>();
let callbacks: RachelChatCallbacks = {};
let draftInstance: Chat<RachelUIMessage> | null = null;

export const setRachelChatCallbacks = (next: RachelChatCallbacks): void => {
  callbacks = next;
};

const buildTransport = (chatId: string | null) =>
  new DefaultChatTransport<RachelUIMessage>({
    api: API,
    headers: () => getRachelRequestHeaders(),
    prepareSendMessagesRequest: ({ messages, body }) => {
      if (!chatId) {
        throw new Error('chatId é obrigatório para enviar mensagens da Rachel');
      }
      return { body: { ...body, chatId, messages: messages.slice(-1) } };
    },
  });

interface SendAutomaticallyOptions {
  messages: RachelUIMessage[];
}

const isAskUserAnswered = (options: SendAutomaticallyOptions): boolean => {
  const lastMessage = options.messages.at(-1);
  if (!lastMessage || lastMessage.role !== 'assistant') {
    return false;
  }

  return (
    !!findAnsweredAskUserPart(lastMessage) && lastAssistantMessageIsCompleteWithToolCalls(options)
  );
};

export const shouldSendRachelAutomatically = (options: SendAutomaticallyOptions): boolean =>
  lastAssistantMessageIsCompleteWithApprovalResponses(options) || isAskUserAnswered(options);

const buildChat = (chatId: string | null): Chat<RachelUIMessage> =>
  new Chat<RachelUIMessage>({
    id: chatId ?? DRAFT_CHAT_ID,
    transport: buildTransport(chatId),
    sendAutomaticallyWhen: shouldSendRachelAutomatically,
    onData: (part) => callbacks.onData?.(part),
    onError: (error) => callbacks.onError?.(error),
    onFinish: (event) => callbacks.onFinish?.(event, chatId),
  });

export const getOrCreateRachelChatInstance = (chatId: string): Chat<RachelUIMessage> => {
  const existing = instances.get(chatId);
  if (existing) {
    return existing;
  }
  const created = buildChat(chatId);
  instances.set(chatId, created);
  return created;
};

export const hasRachelChatInstance = (chatId: string): boolean => instances.has(chatId);

export const removeRachelChatInstance = (chatId: string): void => {
  instances.delete(chatId);
};

export const clearRachelChatInstances = (): void => {
  instances.clear();
  draftInstance = null;
};

export const getDraftRachelChatInstance = (): Chat<RachelUIMessage> => {
  draftInstance ??= buildChat(null);
  return draftInstance;
};
